import { DateFormat } from '@equip.health/ui';
import { DateTime } from 'luxon';
import { datadogRum } from '@datadog/browser-rum';

import {
  AppointmentTimeIcon,
  CalendarListTypeOption,
} from '~/lib/constants/appointment.constants';

import { getAppointmentAttendees } from '../appointment.util';
import {
  getDurationInMinutes,
  Recurrence,
  showCancelButton,
  showZoomButton,
} from '../calendar.util';
import {
  isUserAttendingEvent,
  showGroupClassEventZoomButton,
} from '../classes/groupClasses.util';
import { getNameParts } from '../util';
import { NoteStatus } from '../../constants/AddPatientNoteModal.constants';
import { ScheduleMode } from '../../constants/schedule.constants';
import {
  AppointmentEventOption,
  GroupClassEventOption,
  TreatmentProgramCode,
} from '../../constants/scheduling.constants';
import { getPreferredFullName } from '../patient.util';
import { isScheduledItemRecurring } from '~/lib/util/schedule/isScheduledItemRecurring';

export enum ScheduleType {
  APPOINTMENT = 'Appointment',
  GROUP_CLASS = 'Group Class',
  GROUP_CLASS_EVENT = 'Group Class Event',
}

const getScheduleAttendeeFromAppointmentAttendee = (
  appointmentAttendee: AppointmentAttendee,
  isHost?: boolean,
): ScheduleAttendee => {
  if (!appointmentAttendee) return null;

  return {
    ...appointmentAttendee,
    ...getNameParts(appointmentAttendee.name),
    isHost,
    isShowAvatar: true,
    type:
      appointmentAttendee.providerType ??
      appointmentAttendee.relationToPatient ??
      'Patient',
  } as GroupClassEventAttendee;
};

export const getScheduleAttendeesFromAppointmentAttendees = (
  appointmentAttendees: AppointmentAttendee[],
): ScheduleAttendee[] => {
  if (!appointmentAttendees || appointmentAttendees.length === 0) return null;

  return appointmentAttendees.map((attendee) =>
    getScheduleAttendeeFromAppointmentAttendee(attendee),
  );
};

type ApptOrGroupNoteStatusAndAuthor = {
  groupClassEventExternalId?: string;
  noteStatus: string;
  authoredByProviderExternalId: string;
  appointmentExternalId?: string;
};

// TODO improve: in this context, 'event' is an ambiguous term to refer to either a group class event or a patient appointment
export const getNoteStatus = (
  eventId: string,
  eventsWithNotesData: ApptOrGroupNoteStatusAndAuthor[],
  isGroupNote: boolean,
): {
  isNoteInSignedState: boolean;
  isNoteInDraftState: boolean;
  authoredByProviderId?: string;
} => {
  if (isGroupNote) {
    const note = eventsWithNotesData?.find(
      (note) => note.groupClassEventExternalId === eventId,
    );

    if (note) {
      return {
        isNoteInDraftState: note.noteStatus !== NoteStatus.SIGNED,
        isNoteInSignedState: note.noteStatus === NoteStatus.SIGNED,
        authoredByProviderId: note.authoredByProviderExternalId,
      };
    }
  } else {
    const note = eventsWithNotesData?.filter(
      (note) => note.appointmentExternalId === eventId,
    );
    if (note?.length > 1) {
      return {
        isNoteInDraftState: false,
        isNoteInSignedState: true,
      };
    } else if (note?.length === 1) {
      const signed = note[0].noteStatus === NoteStatus.SIGNED;
      return {
        isNoteInDraftState: !signed,
        isNoteInSignedState: signed,
        authoredByProviderId: note[0].authoredByProviderExternalId,
      };
    } else {
      return {
        isNoteInDraftState: false,
        isNoteInSignedState: false,
      };
    }
  }

  return {
    isNoteInDraftState: false,
    isNoteInSignedState: false,
  };
};

export const getScheduleEventsFromGroupClassEvents = (
  groupClassEvents: GroupClassEventsBase[],
  userId: string,
  groupClassEventsWithNotesData: {
    groupClassEventExternalId: string;
    authoredByProviderExternalId: string;
    noteStatus: string;
  }[],
): ScheduleItemBase[] => {
  return (groupClassEvents ?? []).map((groupClassEvent) => {
    const { isNoteInSignedState, isNoteInDraftState, authoredByProviderId } =
      getNoteStatus(
        groupClassEvent.groupClassEventExternalId,
        groupClassEventsWithNotesData,
        true,
      );

    return {
      attendees: groupClassEvent.attendees,
      color: 'marigold',
      declinedUsers: groupClassEvent.declinedUsers,
      duration: getDurationInMinutes(
        groupClassEvent.startTime,
        groupClassEvent.endTime,
      ),
      endDateTime: groupClassEvent.endTime,
      externalId: groupClassEvent.groupClassEventExternalId,
      frequency: groupClassEvent.frequency,
      groupClassExternalId: groupClassEvent.groupClassExternalId,
      hosts: groupClassEvent.hosts,
      icon: AppointmentTimeIcon.GROUP,
      isNoteInDraftState,
      isRecurring: isScheduledItemRecurring(groupClassEvent.frequency),
      isNoteInSignedState,
      isShowZoomButton: showGroupClassEventZoomButton({
        end: groupClassEvent.endTime,
        isAttendee: isUserAttendingEvent(
          groupClassEvent?.attendees as GroupClassEventAttendee[],
          userId,
        ),
        isHost: (groupClassEvent?.hosts ?? []).some(
          ({ externalId }: ScheduleUserBase) => externalId === userId,
        ),
        start: groupClassEvent.startTime,
      }),
      startDateTime: groupClassEvent.startTime,
      status: 'scheduled',
      title: groupClassEvent.title,
      typeOf: 'Group Class Event',
      noteAuthoredByProviderId: authoredByProviderId,
    } as ScheduleItemBase;
  });
};

const formatTime = (dateTime: DateTime, format: string = 'h:mm a'): string =>
  dateTime.toFormat(format).replace('m ', '').toLowerCase();

export const formatAppointmentTimeRange = (
  start: string,
  end: string,
  tzid?: string,
): string => {
  const startTime = DateTime.fromISO(start).setZone(tzid);
  const endTime = DateTime.fromISO(end).setZone(tzid);

  if (!startTime.isValid || !endTime.isValid) return '';
  const sameMeridiem = startTime.toFormat('a') === endTime.toFormat('a');
  return sameMeridiem
    ? `${formatTime(startTime, 'h:mm')} - ${formatTime(endTime)}`
    : `${formatTime(startTime)} - ${formatTime(endTime)}`;
};

export const getScheduleEventsFromAppointments = (
  appointments: AppointmentDetailResponse[],
  userId: string,
  appointmentEventsWithNotesData: {
    noteStatus: string;
    authoredByProviderExternalId: string;
    appointmentExternalId: string;
  }[],
  isNew?: boolean,
): ScheduleItemBase[] => {
  const getColor = (appointment: AppointmentDetailResponse) => {
    let color = 'primary.100';
    let border = null;
    if (isNew) {
      color = appointment.isCancelled ? '#FAE9E8' : 'primary.10';
      border = appointment.isCancelled ? '#C61910' : '#4CA5A1';
    }
    return { color, border };
  };

  return (appointments ?? []).map((appointment) => {
    const { isNoteInSignedState, isNoteInDraftState, authoredByProviderId } =
      getNoteStatus(
        appointment.appointmentExternalId,
        appointmentEventsWithNotesData,
        false,
      );

    const shouldShowZoomButton = showZoomButton({
      end: appointment.appointmentEndDateTime,
      hostParticipantId: appointment.host.externalId,
      providerParticipants: appointment.providerAttendees ?? [],
      start: appointment.appointmentStartDateTime,
      userId,
    });

    return {
      attendees: (
        getScheduleAttendeesFromAppointmentAttendees(
          getAppointmentAttendees(appointment),
        ) ?? []
      )
        .concat([
          getScheduleAttendeeFromAppointmentAttendee(appointment.host, true),
        ])
        .reverse(),
      ...getColor(appointment),
      isNew: isNew ?? false,
      duration: getDurationInMinutes(
        appointment.appointmentStartDateTime,
        appointment.appointmentEndDateTime,
      ),
      endDateTime: appointment.appointmentEndDateTime,
      externalId: appointment.appointmentExternalId,
      frequency: appointment.recurrence,
      hosts: [
        getScheduleAttendeeFromAppointmentAttendee(appointment.host, true),
      ].filter(Boolean) as ScheduleUserBase[],
      icon: AppointmentTimeIcon.CALENDAR,
      isNoteInDraftState,
      isNoteInSignedState,
      isRecurring: isScheduledItemRecurring(appointment.recurrence),
      isShowZoomButton:
        shouldShowZoomButton &&
        appointment.hasZoomCall &&
        !appointment.isCancelled,
      patient: {
        ...appointment.patient,
        ...getNameParts(appointment.patient?.name),
        userId: appointment.patient?.externalId,
      } as Patient,
      recurringUntilDateTime: appointment?.recurringUntilDateTime,
      startDateTime: appointment.appointmentStartDateTime,
      status: appointment.appointmentStatus,
      title: appointment.appointmentType,
      typeOf: 'Appointment',
      noteAuthoredByProviderId: authoredByProviderId,
      eventId: appointment.eventId,
      createdAt: appointment.createdAt,
      isCancelled: appointment.isCancelled,
      cancelledReason: appointment.cancellationReason,
    } as ScheduleItemBase;
  });
};

/**      Util functions */
export const getRecurrenceDisplay = (
  start: string,
  recurrence: string,
  recurringUntilDateTime: string,
  ShowUpdatedRecurrenceDetails?: boolean,
  shouldShowEndDate?: boolean,
  tzid?: string,
): string => {
  if (!recurrence) return null;

  const recurringUntilDateTimeConverted: DateTime = DateTime.fromISO(
    recurringUntilDateTime,
  ).setZone(tzid);

  let recurringUntilDateTimeString = '';
  if (recurringUntilDateTimeConverted.isValid) {
    recurringUntilDateTimeString =
      recurringUntilDateTime && shouldShowEndDate
        ? ` until ${recurringUntilDateTimeConverted.toFormat(
            DateFormat.shortMonthDayYear,
          )}`
        : '';
  }

  const dayOfWeek = DateTime.fromISO(start).setZone(tzid).toFormat('cccc');

  if (recurrence.toLocaleLowerCase() === Recurrence.WEEKLY.toLocaleLowerCase())
    return `${
      ShowUpdatedRecurrenceDetails ? 'Every' : 'Recurring, weekly on'
    } ${dayOfWeek}${recurringUntilDateTimeString}`;
  if (
    recurrence.toLocaleLowerCase() === Recurrence.BIWEEKLY.toLocaleLowerCase()
  )
    return `${ShowUpdatedRecurrenceDetails ? 'Every other' : 'Recurring, every two weeks on'} ${dayOfWeek}${recurringUntilDateTimeString}`;
  if (recurrence.toLocaleLowerCase() === Recurrence.MONTHLY.toLocaleLowerCase())
    return `${ShowUpdatedRecurrenceDetails ? 'Every 4th' : 'Recurring, every 4 weeks on'} ${dayOfWeek}${recurringUntilDateTimeString}`;
  return null;
};

export const formatFullDateString = (date: DateTime): string => {
  return date ? date.toLocaleString(DateTime.DATE_FULL) : '';
};

const dateHeaderByScheduleViewMapper: Record<
  ScheduleMode,
  (date: DateTime) => string
> = {
  [ScheduleMode.DAY]: (date: DateTime) => formatFullDateString(date),
  [ScheduleMode.WEEK]: (date: DateTime) => {
    const monthOfStartWeek = date.startOf('week').monthLong;
    const monthOfEndWeek = date.endOf('week').monthLong;
    const yearOfEndWeek = date.endOf('week').year;
    return `${monthOfStartWeek}${
      monthOfStartWeek !== monthOfEndWeek ? ` - ${monthOfEndWeek}` : ''
    } ${yearOfEndWeek}`;
  },
};

export const getScheduleViewDateHeader = (
  tab: ScheduleMode,
  date: DateTime,
): string => {
  return dateHeaderByScheduleViewMapper[tab](date);
};

export const getScheduleViewDateHeaderV2 = (
  selectedTab: AppointmentListType,
  currentDate: DateTime,
): string => {
  if (selectedTab === CalendarListTypeOption.DAILY_VIEW) {
    return currentDate.toFormat(DateFormat.fullMonthDayYear);
  }
  const monthOfStartWeek = currentDate.startOf('week').monthLong;
  const monthOfEndWeek = currentDate.endOf('week').monthLong;
  const yearOfEndWeek = currentDate.endOf('week').year;
  return `${monthOfStartWeek}${
    monthOfStartWeek !== monthOfEndWeek ? ` - ${monthOfEndWeek}, ` : ', '
  } ${yearOfEndWeek}`;
};

export const checkOverlapScheduledItem = (
  sourceScheduledItem: ScheduleItemBase,
  targetScheduledItem: ScheduleItemBase,
): boolean => {
  if (sourceScheduledItem && targetScheduledItem) {
    const { startDateTime: start, endDateTime: end } = sourceScheduledItem;
    const { startDateTime: targetStart, endDateTime: targetEnd } =
      targetScheduledItem;
    return start < targetEnd && end > targetStart;
  }

  return false;
};

export const getDisplayTitle = (
  scheduledItem: ScheduleItemBase,
  isProvider: boolean,
): string => {
  const { patient, title, typeOf } = scheduledItem;
  const { chosenName, firstName, lastName } = patient ?? {};
  return typeOf === ScheduleType.APPOINTMENT && isProvider
    ? `${getPreferredFullName(chosenName, firstName, lastName, false)} - ${title}`
    : title;
};
export const getIsDeclineGroupClassEventAllowed = (
  isHost: boolean,
  isAttendee: boolean,
  isOnlyHost: boolean,
  isPastEvent: boolean,
): boolean => {
  const isCurrentUserHost = isHost && isOnlyHost;

  if (isCurrentUserHost || isPastEvent) return false;

  return isHost || isAttendee;
};

export const getGroupClassEventOptions = (
  isHost: boolean,
  isAttendee: boolean,
  isSchedulingAdmin: boolean,
  isPastEvent?: boolean,
  isPatientPath?: boolean,
  isOnlyHost?: boolean,
): Record<
  GroupClassEventOption,
  { isDisabled: boolean; isHidden: boolean }
> => ({
  [GroupClassEventOption.ADD_HOST]: {
    isDisabled: isPastEvent,
    isHidden: isPatientPath || (!isHost && !isSchedulingAdmin),
  },
  [GroupClassEventOption.ADD_NOTE]: {
    isDisabled: !isHost,
    isHidden: false,
  },
  [GroupClassEventOption.CANCEL]: {
    isDisabled: isPastEvent,
    isHidden: isPatientPath || (!isHost && !isSchedulingAdmin),
  },
  [GroupClassEventOption.DECLINE]: {
    isDisabled: isPastEvent,
    isHidden:
      isPatientPath ||
      !getIsDeclineGroupClassEventAllowed(
        isHost,
        isAttendee,
        isOnlyHost,
        isPastEvent,
      ),
  },
  [GroupClassEventOption.VIEW_CLASS_INFO]: {
    isDisabled: false,
    isHidden: false,
  },
});

export const getAppointmentEventOptions = (
  isHost: boolean,
  isSchedulingAdmin: boolean,
  isAttendee?: boolean,
): Record<
  AppointmentEventOption,
  { isDisabled: boolean; isHidden: boolean }
> => ({
  [AppointmentEventOption.ADD_NOTE]: {
    isDisabled: !isAttendee && !isHost,
    isHidden: !isHost && !isSchedulingAdmin,
  },
  [AppointmentEventOption.CANCEL]: {
    isDisabled: false,
    isHidden: !showCancelButton(isHost, isSchedulingAdmin),
  },
  [AppointmentEventOption.EDIT]: { isDisabled: true, isHidden: false },
});

export const getScheduleItemFromExternalId = (
  scheduledItems: ScheduleItemBase[],
  externalId: string,
): ScheduleItemBase => {
  return (
    (scheduledItems ?? []).find((item) => item.externalId === externalId) ||
    null
  );
};

/** Combines and sorts appointments and group class events */
export const deriveScheduleItems = (
  userId: string,
  apptsData: GetAppointmentDetailsResponse['data'],
  classEventsData: FetchGroupClassEventsResponse['data']['data'],
  noteIdsAndStatuses: ApptOrGroupNoteStatusAndAuthor[],
  isNew?: boolean,
): ScheduleItemBase[] => {
  return [
    ...getScheduleEventsFromAppointments(
      apptsData,
      userId,
      noteIdsAndStatuses.filter((item) => !!item.appointmentExternalId) as {
        noteStatus: string;
        authoredByProviderExternalId: string;
        appointmentExternalId: string;
      }[],
      isNew,
    ),
    ...getScheduleEventsFromGroupClassEvents(
      classEventsData,
      userId,
      noteIdsAndStatuses.filter((item) => !!item.groupClassEventExternalId) as {
        noteStatus: string;
        authoredByProviderExternalId: string;
        groupClassEventExternalId: string;
      }[],
    ),
  ].sort(
    (a, b) =>
      new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime(),
  );
};

/** Find the earlier of two dates */
export const getNextScheduleEventStartDate = (
  eventA: string,
  eventB: string,
): string => {
  if (eventA && eventB) {
    const isNextApptStartFirst =
      new Date(eventA).getTime() < new Date(eventB).getTime();

    return isNextApptStartFirst ? eventA : eventB;
  }

  return eventA ?? eventB;
};

export const getLastScheduleEventStartDate = (
  eventA: string,
  eventB: string,
): string => {
  if (eventA && eventB) {
    const isNextApptStartFirst =
      new Date(eventA).getTime() > new Date(eventB).getTime();

    return isNextApptStartFirst ? eventA : eventB;
  }

  return eventA ?? eventB;
};

export const isAddNoteAllowed = (
  scheduleType: ScheduleTypeValue,
  isHost: boolean,
  isAttendee: boolean,
  isSchedulingAdmin: boolean,
): { isHidden: boolean; isDisabled: boolean } => {
  if (scheduleType === ScheduleType.GROUP_CLASS_EVENT) {
    const groupClassAddNoteOption = getGroupClassEventOptions(
      isHost,
      isAttendee,
      isSchedulingAdmin,
    )[GroupClassEventOption.ADD_NOTE];

    return {
      isDisabled: groupClassAddNoteOption.isDisabled,
      isHidden: false,
    };
  }
  if (scheduleType === ScheduleType.APPOINTMENT) {
    const appointmentAddNoteOption = getAppointmentEventOptions(
      isHost,
      isSchedulingAdmin,
      isAttendee,
    )[AppointmentEventOption.ADD_NOTE];

    return {
      isDisabled: appointmentAddNoteOption.isDisabled,
      isHidden: appointmentAddNoteOption.isHidden,
    };
  }

  return { isDisabled: false, isHidden: false };
};

export const noEligibleHostErrorMessage = (providerType: string) => {
  return `This appointment type can only be hosted by a ${providerType}. Please make adjustments to the patient’s care team or to this appointment’s participants in order to book.`;
};

export const getCurrentAppointmentDetails = (
  startDateTime: string,
  frequency: string,
  recurringUntilDateTime: string,
  tzid?: string,
): string => {
  const recurrenceText = getRecurrenceDisplay(
    startDateTime,
    frequency,
    recurringUntilDateTime,
    true,
    false,
  );

  if (recurrenceText) {
    return recurrenceText;
  }
  const startDateTimeObj = DateTime.fromISO(startDateTime).setZone(tzid);
  if (!startDateTimeObj.isValid) return '';

  const dayAndDate = startDateTimeObj.toFormat('cccc');
  const date = startDateTimeObj.toFormat(DateFormat.shortMonthDay);

  return `${dayAndDate} ${date}`;
};

/**
 * Map display name to backend TreatmentProgramCode enum value.
 * Display names come from clinicalProfile.programName in the format used by EpisodeTreatmentProgram.
 */
const TREATMENT_PROGRAM_MAP: Record<string, TreatmentProgramCode> = {
  'equip treatment': TreatmentProgramCode.EQUIP_TREATMENT,
  freeform: TreatmentProgramCode.FREEFORM,
  'relapse prevention': TreatmentProgramCode.RELAPSE_PREVENTION,
  'equip adult': TreatmentProgramCode.EQUIP_ADULT,
  'open care model': TreatmentProgramCode.OPEN_CARE_MODEL,
  'transition track': TreatmentProgramCode.TRANSITION,
};

export const getTreatmentProgramCode = (
  treatmentProgram: string,
): TreatmentProgramCode => {
  const result = TREATMENT_PROGRAM_MAP[treatmentProgram?.toLowerCase()];

  if (!result && treatmentProgram) {
    datadogRum.addError(
      new Error(
        `Unknown treatment program: "${treatmentProgram}". Falling back to EQUIP_TREATMENT.`,
      ),
      {
        extra: {
          treatmentProgram,
          fallback: TreatmentProgramCode.EQUIP_TREATMENT,
        },
        tags: { logFrom: 'getTreatmentProgramCode' },
      },
    );
    return TreatmentProgramCode.EQUIP_TREATMENT;
  }

  return result ?? TreatmentProgramCode.EQUIP_TREATMENT;
};
/** END: Util functions */
